/**
 * GSTIN checks that need no network.
 *
 * A GSTIN is not just fifteen characters in the right shape. The first two
 * digits are the registering state, the next ten are the holder's PAN, and the
 * last is a check digit computed over the other fourteen. A regex alone let
 * through GSTINs with a mistyped digit, another person's PAN inside, or a state
 * the seller does not ship from — each of which the GST portal would reject the
 * moment an invoice or a TCS return quoted it.
 */
import { INDIAN_STATES } from './india';

const GSTIN_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const GSTIN_SHAPE = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

/**
 * The two-letter codes sellers and addresses use, to the GST state code.
 * Includes the common alternates (OD/OR, TS/TG, UK/UT, CG/CT) people type.
 */
const LETTER_TO_GST: Readonly<Record<string, string>> = Object.freeze({
  AP: '37',
  AR: '12',
  AS: '18',
  BR: '10',
  CG: '22',
  CT: '22',
  GA: '30',
  GJ: '24',
  HR: '06',
  HP: '02',
  JH: '20',
  KA: '29',
  KL: '32',
  MP: '23',
  MH: '27',
  MN: '14',
  ML: '17',
  MZ: '15',
  NL: '13',
  OR: '21',
  OD: '21',
  PB: '03',
  RJ: '08',
  SK: '11',
  TN: '33',
  TG: '36',
  TS: '36',
  TR: '16',
  UP: '09',
  UK: '05',
  UT: '05',
  WB: '19',
  AN: '35',
  CH: '04',
  DN: '26',
  DD: '26',
  DL: '07',
  JK: '01',
  LA: '38',
  LD: '31',
  PY: '34',
});

/**
 * Every state and union territory with the two-letter code addresses store,
 * in the statutory order — for a state picker. The first letter code listed
 * for a state wins (OR over OD, TG over TS), so each state appears once.
 */
export const INDIAN_STATE_OPTIONS: ReadonlyArray<{ code: string; name: string }> = Object.freeze(
  INDIAN_STATES.flatMap((st) => {
    const letter = Object.keys(LETTER_TO_GST).find((l) => LETTER_TO_GST[l] === st.code);
    return letter ? [{ code: letter, name: st.name }] : [];
  }),
);

/** The GST state code for a two-letter or two-digit state code, if it is one. */
export function gstStateCode(code: string | null | undefined): string | undefined {
  const c = (code ?? '').trim().toUpperCase();
  if (/^\d{2}$/.test(c)) return INDIAN_STATES.some((s) => s.code === c) ? c : undefined;
  return LETTER_TO_GST[c];
}

/** The check digit the fifteenth character of a GSTIN must be. */
export function gstinCheckDigit(first14: string): string {
  let sum = 0;
  for (let i = 0; i < 14; i += 1) {
    const value = GSTIN_CHARS.indexOf(first14[i]!);
    const product = value * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(product / 36) + (product % 36);
  }
  return GSTIN_CHARS[(36 - (sum % 36)) % 36]!;
}

export interface GstinCheck {
  /** Normalised: trimmed and upper-cased. */
  gstin: string;
  valid: boolean;
  /** Why it fails, in words a seller can act on. Empty when valid. */
  problems: string[];
  /** Two-digit state the GSTIN is registered in. */
  stateCode?: string;
  stateName?: string;
  /** The PAN inside the GSTIN. */
  pan?: string;
}

/**
 * Check a GSTIN, and optionally that it belongs to this PAN and this state.
 *
 * The PAN and state checks are what make a GSTIN the seller's own: a real,
 * valid GSTIN copied from someone else passes every other test.
 */
export function checkGstin(
  input: string,
  expect: { pan?: string | null; stateCode?: string | null } = {},
): GstinCheck {
  const gstin = (input ?? '').trim().toUpperCase();
  const problems: string[] = [];

  if (!GSTIN_SHAPE.test(gstin)) {
    return {
      gstin,
      valid: false,
      problems: ['A GSTIN is 15 characters, like 27ABCDE1234F1Z5.'],
    };
  }

  const stateCode = gstin.slice(0, 2);
  const state = INDIAN_STATES.find((s) => s.code === stateCode);
  if (!state) problems.push(`${stateCode} is not an Indian state code.`);

  if (gstinCheckDigit(gstin.slice(0, 14)) !== gstin[14]) {
    problems.push('The last character does not match — a digit is probably mistyped.');
  }

  const pan = gstin.slice(2, 12);
  const expectedPan = expect.pan?.trim().toUpperCase();
  if (expectedPan && expectedPan !== pan) {
    problems.push(`This GSTIN belongs to PAN ${pan}, not to the PAN given (${expectedPan}).`);
  }

  const expectedState = gstStateCode(expect.stateCode);
  if (expect.stateCode && expectedState && expectedState !== stateCode) {
    problems.push(
      `This GSTIN is registered in ${state?.name ?? stateCode}, but the pickup address is in ${
        INDIAN_STATES.find((s) => s.code === expectedState)?.name ?? expect.stateCode
      }. Use the GSTIN for the state you ship from.`,
    );
  }

  return {
    gstin,
    valid: problems.length === 0,
    problems,
    stateCode,
    ...(state ? { stateName: state.name } : {}),
    pan,
  };
}
